import { useQueryClient } from "@tanstack/react-query";
import { Form, Input, Modal, Select, Switch } from "antd";
import { useEffect } from "react";
import { useOrganizationTypePositions } from "@/modules/organization-type-positions/hooks/useOrganizationTypePositions";
import { useUpdateUser } from "@/modules/users/hooks/useUsers";
import { PhoneInput } from "@/shared/components/PhoneInput";
import {
	orgUserKeys,
	useCreateOrganizationUser,
	useUpdateOrganizationUser,
} from "../hooks/useOrganizationUsers";
import { useOrgUsersStore } from "../store/org-users.store";

interface OrgUserFormValues {
	fullname?: string;
	phone?: string;
	password?: string;
	positionId?: string;
	isPrimary?: boolean;
}

interface OrgUserFormModalProps {
	organizationId: string;
}

export function OrgUserFormModal({ organizationId }: OrgUserFormModalProps) {
	const [form] = Form.useForm<OrgUserFormValues>();
	const queryClient = useQueryClient();
	const { modalOpen, editingItem, closeModal } = useOrgUsersStore();

	const { mutate: create, isPending: isCreating } = useCreateOrganizationUser(organizationId);
	const { mutate: updateOrgUser, isPending: isUpdatingOrgUser } =
		useUpdateOrganizationUser(organizationId);
	const { mutate: updateUser, isPending: isUpdatingUser } = useUpdateUser();

	const { data: positionsData, isLoading: positionsLoading } = useOrganizationTypePositions(
		{ page: 1, limit: 10 },
		{ enabled: modalOpen }
	);

	useEffect(() => {
		if (editingItem) {
			form.setFieldsValue({
				fullname: editingItem.user?.fullname,
				phone: editingItem.user?.phone,
				positionId: editingItem.position.id,
				isPrimary: editingItem.isPrimary,
			});
		} else {
			form.resetFields();
		}
	}, [editingItem, form]);

	const handleClose = () => {
		closeModal();
		form.resetFields();
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: edit flow has 3 branches (user only, org only, both)
	const handleEditSubmit = (values: OrgUserFormValues) => {
		if (!editingItem) {
			return;
		}

		const userUpdateData: { fullname?: string; phone?: string; password?: string } = {};
		if (values.fullname !== editingItem.user?.fullname) {
			userUpdateData.fullname = values.fullname;
		}
		if (values.phone !== editingItem.user?.phone) {
			userUpdateData.phone = values.phone;
		}
		if (values.password) {
			userUpdateData.password = values.password;
		}

		const userId = editingItem.user?.id;
		const hasUserChanges = Object.keys(userUpdateData).length > 0 && userId;
		const orgData = {
			positionId: values.positionId ?? editingItem.position.id,
			isPrimary: values.isPrimary,
		};
		const hasOrgChanges =
			orgData.positionId !== editingItem.position.id || orgData.isPrimary !== editingItem.isPrimary;

		const runBoth = () =>
			Promise.all([
				new Promise<void>((resolve, reject) => {
					updateOrgUser(
						{ id: editingItem.id, data: orgData },
						{ onSuccess: () => resolve(), onError: reject }
					);
				}),
				new Promise<void>((resolve, reject) => {
					updateUser(
						{ id: userId, data: userUpdateData },
						{ onSuccess: () => resolve(), onError: reject }
					);
				}),
			]).then(handleClose);

		if (hasUserChanges && hasOrgChanges) {
			runBoth().catch(() => {
				/* Errors shown by mutation onError */
			});
		} else if (hasUserChanges) {
			updateUser(
				{ id: userId, data: userUpdateData },
				{
					onSuccess: () => {
						queryClient.invalidateQueries({ queryKey: orgUserKeys.lists() });
						handleClose();
					},
				}
			);
		} else {
			updateOrgUser({ id: editingItem.id, data: orgData }, { onSuccess: handleClose });
		}
	};

	const handleCreateSubmit = (values: OrgUserFormValues) => {
		const { fullname, phone, password, positionId, isPrimary } = values;
		const hasRequired = fullname && phone && password && positionId;
		if (!hasRequired) {
			return;
		}
		create({ fullname, phone, password, positionId, isPrimary }, { onSuccess: handleClose });
	};

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			editingItem ? handleEditSubmit(values) : handleCreateSubmit(values);
		});
	};

	return (
		<Modal
			title={editingItem ? "Xodimni tahrirlash" : "Xodim qo'shish"}
			open={modalOpen}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={isCreating || isUpdatingOrgUser || isUpdatingUser}
			okText={editingItem ? "Saqlash" : "Qo'shish"}
			cancelText="Bekor qilish"
			destroyOnHidden
			width={720}
		>
			<Form form={form} layout="vertical" requiredMark={false} className="mt-4">
				{/* To'liq ism va telefon — create yoki edit */}
				<div className="grid grid-cols-2 gap-4">
					<Form.Item
						name="fullname"
						label="To'liq ism"
						rules={[{ required: true, message: "Ismni kiriting" }]}
					>
						<Input placeholder="Abdullayev Sardor" />
					</Form.Item>
					<Form.Item
						name="phone"
						label="Telefon raqam"
						rules={[
							{ required: true, message: "Telefon raqamni kiriting" },
							{ pattern: /^\+998\d{9}$/, message: "To'liq 9 ta raqam kiriting" },
						]}
					>
						<PhoneInput placeholder="90 123 45 67" />
					</Form.Item>
				</div>

				{/* Parol va lavozim */}
				<div className="grid grid-cols-2 gap-4">
					<Form.Item
						name="password"
						label={editingItem ? "Yangi parol (ixtiyoriy)" : "Parol"}
						rules={
							editingItem
								? []
								: [
										{ required: true, message: "Parolni kiriting" },
										{ min: 4, message: "Kamida 4 ta belgi" },
									]
						}
					>
						<Input.Password
							placeholder={editingItem ? "O'zgartirmasangiz bo'sh qoldiring" : "Parol"}
							autoComplete="new-password"
						/>
					</Form.Item>
					<Form.Item
						name="positionId"
						label="Lavozim"
						rules={[{ required: true, message: "Lavozimni tanlang" }]}
					>
						<Select
							placeholder="Tanlang"
							loading={positionsLoading}
							showSearch
							filterOption={(input, option) =>
								(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
							}
							options={positionsData?.data?.items?.map((p) => ({
								value: p.id,
								label: `${p.name} — ${p.type?.name ?? ""}`,
							}))}
						/>
					</Form.Item>
					<Form.Item name="isPrimary" label="Asosiy xodim" valuePropName="checked">
						<Switch />
					</Form.Item>
				</div>
			</Form>
		</Modal>
	);
}
